import React, { useMemo, useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import { Button, Input } from "reactstrap"
import { toast } from "react-toastify"

import Breadcrumbs from "../../../components/Common/Breadcrumb"
import TableContainer from "../../../components/Common/DataTableContainer"
import {
  fetchBazaarList,
  getPaymentStatistics,
  updateBazaarActiveStatus,
} from "../../../redux/actions/pvs"
import { UPDATE_BAZAAR_ACTIVE_STATUS_RESET } from "../../../redux/types/pvs"
import ActionDropdown from "components/ActionDropdown"

const AllBazaarList = () => {
  document.title = "PULA Bazaar List"

  const history = useNavigate()
  const dispatch = useDispatch()
  const token = useSelector(state => state.auth.token)
  const bazaarListState = useSelector(state => state.pvs.bazaarList)
  const updateBazaarActiveStatusScreen = useSelector(
    state => state.screens.pvs.updateBazaarActiveStatus
  )
  const [bazaarData, setBazaarData] = useState([])
  const [togglingId, setTogglingId] = useState(null)

  // Fetch Bazaar List API
  useEffect(() => {
    dispatch(fetchBazaarList({ offset: 1 }, token))
    dispatch(getPaymentStatistics({}))
  }, [])

  useEffect(() => {
    if (bazaarListState && bazaarListState.length > 0) {
      const formattedData = bazaarListState.map((item, index) => ({
        ...item,
        id: item.enc_bazaar_id,
        full_name: item.bazaar_name || "-",
        formated_start_date: new Date(item.start_date).toLocaleDateString(),
        formated_end_date: new Date(item.end_date).toLocaleDateString(),
        start_date: item.start_date,
        end_date: item.end_date,
        stall_count: item.total_stalls || "-",
        created_date: item.created_date
          ? new Date(item.created_date).toLocaleDateString()
          : "-",
        location: item.location || "-",
        description: item.description || "-",
        is_active: Number(item.is_active) === 1,
      }))

      setBazaarData(formattedData)
    }
  }, [bazaarListState])

  useEffect(() => {
    if (updateBazaarActiveStatusScreen.success) {
      toast.success(updateBazaarActiveStatusScreen.message)
      setTogglingId(null)
      dispatch({ type: UPDATE_BAZAAR_ACTIVE_STATUS_RESET })
    }
    if (updateBazaarActiveStatusScreen.error) {
      toast.error(updateBazaarActiveStatusScreen.error)
      // revert the optimistic toggle
      setBazaarData(prev =>
        prev.map(row =>
          row.id === togglingId ? { ...row, is_active: !row.is_active } : row
        )
      )
      setTogglingId(null)
      dispatch({ type: UPDATE_BAZAAR_ACTIVE_STATUS_RESET })
    }
  }, [updateBazaarActiveStatusScreen])

  const handleToggleActive = row => {
    const nextActive = !row.is_active
    setTogglingId(row.id)
    setBazaarData(prev =>
      prev.map(item =>
        item.id === row.id ? { ...item, is_active: nextActive } : item
      )
    )
    dispatch(
      updateBazaarActiveStatus({
        enc_bazaar_id: row.id,
        is_active: nextActive,
      })
    )
  }

  const columns = useMemo(
    () => [
      {
        header: "Bazaar Name",
        accessorKey: "full_name",
        enableColumnFilter: true,
        enableSorting: true,
      },
      {
        header: "Start Date",
        accessorKey: "formated_start_date",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "End Date",
        accessorKey: "formated_end_date",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Total Stall",
        accessorKey: "total_stalls",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Sold Stall",
        accessorKey: "sold_stalls",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Available Stall",
        accessorKey: "available_stalls",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Created Date",
        accessorKey: "created_date",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Active",
        accessorKey: "is_active",
        enableColumnFilter: false,
        enableSorting: false,
        cell: info => {
          const row = info.row.original
          return (
            <div
              className="form-check form-switch"
              onClick={e => e.stopPropagation()}
            >
              <Input
                type="checkbox"
                className="form-check-input"
                role="switch"
                checked={row.is_active}
                disabled={togglingId === row.id}
                onChange={() => handleToggleActive(row)}
              />
            </div>
          )
        },
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: info => {
          const row = info.row.original

          return (
            <div
              className="d-flex align-items-center gap-2"
              onClick={e => e.stopPropagation()}
            >
              {row.available_stalls > 0 && (
                <button
                  className="btn btn-sm btn-success"
                  onClick={() => history(`/bazaar-registration?id=${row.id}`)}
                  title="Register"
                >
                  Booking
                </button>
              )}
              <button
                className="btn btn-sm btn-info"
                onClick={() => history(`/view-booking-list?id=${row.id}`)}
                title="View Booking List"
              >
                Payments
              </button>
              <ActionDropdown buttonLabel="Edit">
                <button
                  className="dropdown-item"
                  onClick={() => history(`/slots-list?id=${row.id}`)}
                >
                  Slots
                </button>

                <button
                  className="dropdown-item"
                  onClick={() => history(`/edit-bazaar?id=${row.id}`)}
                >
                  Bazaar
                </button>
              </ActionDropdown>
              {/* <button
                className="btn btn-sm btn-warning me-2"
                onClick={() => history(`/slots-list`)}
                title="Register"
              >
                View Slots
              </button>
              <button
                className="btn btn-sm btn-danger ms-2"
                onClick={() => history(`/edit-bazaar`)}
                title="Edit Bazaar"
              >
                Edit Bazaar
              </button> */}
            </div>
          )
        },
      },
    ],
    [history, togglingId]
  )

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="d-flex align-items-center justify-content-end mt-3 mb-3">
          <div className="flex-grow-1">
            <h4 className="h4 mb-0 nom-title">All Bazaar List</h4>
          </div>
          <Button
            color="info"
            className="me-2"
            onClick={() => (window.location.href = "/payment-statistics")}
          >
            Payment Statistics
          </Button>
          <Button
            color="primary"
            onClick={() => (window.location.href = "/add-bazaar")}
          >
            Add Bazaar
          </Button>
        </div>

        <TableContainer
          defaultPageSize={20}
          columns={columns}
          data={bazaarData || []}
          isGlobalFilter={true}
          isPagination={true}
          isCustomPageSize={true}
          SearchPlaceholder="Search records..."
          pagination="pagination"
          isAddButton={false}
          buttonClass="btn btn-primary"
          paginationWrapper="dataTables_paginate paging_simple_numbers"
          tableClass="table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          theadClass="table-light"
          divClassName="table-responsive mb-4 nominee-tbl-wrapper"
        />
      </div>
    </div>
  )
}

export default AllBazaarList
